import { Layout, Space, Avatar, Dropdown, Typography } from "antd";
import {
  LogoutOutlined,
  UserOutlined,
  LockOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import { useGetIdentity, useLogout } from "@refinedev/core";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "./LanguageSelector";
import type { UserInfo } from "../types/auth";
import { ADMIN_USER_TYPES, AUDITOR_USER_TYPES } from "../types/auth";

const { Header } = Layout;
const { Text } = Typography;

/**
 * Custom Header — username, language selector, avatar dropdown.
 * The dropdown exposes profile / change-password entries for auditor users
 * (the rest of the org keeps the previous logout-only behaviour).
 */
export const CustomHeader = () => {
  const { data: user } = useGetIdentity<UserInfo>();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const userType = user?.app_user?.user_type ?? -1;
  const isAuditor = (AUDITOR_USER_TYPES as readonly number[]).includes(
    userType,
  );
  const isAdmin = (ADMIN_USER_TYPES as readonly number[]).includes(userType);
  // Base path del área — cada rol tiene su propio prefijo porque las
  // rutas están bajo `AdminRoute` o `AuditorRoute`. El componente que
  // renderiza es el mismo (`AuditorProfile` reusado, con lógica
  // role-aware dentro).
  const profileBase = isAdmin
    ? "/admin/profile"
    : isAuditor
      ? "/auditor/profile"
      : null;

  const avatarUrl = user?.app_user?.avatar_url ?? undefined;
  const displayName =
    (user?.first_name && user?.last_name)
      ? `${user.first_name} ${user.last_name}`
      : user?.app_user?.name || user?.username || t("auth.username");

  const items = [
    ...(profileBase
      ? [
          {
            key: "profile",
            icon: <IdcardOutlined />,
            label: t("header.my_profile"),
            onClick: () => navigate(profileBase),
          },
          {
            key: "change-password",
            icon: <LockOutlined />,
            label: t("header.change_password"),
            onClick: () => navigate(`${profileBase}/password`),
          },
          { type: "divider" as const },
        ]
      : []),
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("auth.logout"),
      onClick: () => logout(),
    },
  ];

  return (
    <Header
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 24px",
        backgroundColor: "#fff",
        borderBottom: "1px solid #f0f0f0",
        position: "sticky",
        top: 0,
        zIndex: 999,
      }}
    >
      <div />
      <Space size="middle">
        <LanguageSelector />
        <Dropdown menu={{ items }}>
          <Space style={{ cursor: "pointer" }}>
            <Avatar
              src={avatarUrl}
              icon={!avatarUrl ? <UserOutlined /> : undefined}
            />
            <Text>{displayName}</Text>
          </Space>
        </Dropdown>
      </Space>
    </Header>
  );
};
